import React, { lazy, Suspense, useEffect, useState } from 'react';
import httpClient from './api/httpClient';
import LandingPage from './components/LandingPage';
import LoginPage from './components/LoginPage';
import PublicLayout from './components/PublicLayout';
import './styles.css';

const MemberPage = lazy(() => import('./components/VlessKeysPage'));
const AUTH_TOKEN_STORAGE_KEY = 'notreen.authToken';
const SESSION_STORAGE_KEY = 'notreen.session';

function applyAuthToken(token) {
  if (token) {
    httpClient.defaults.headers.common.Authorization = `Bearer ${token}`;
    return;
  }
  delete httpClient.defaults.headers.common.Authorization;
}

function readStoredSession() {
  const token = window.sessionStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  if (!token) return null;
  try {
    const storedSession = JSON.parse(window.sessionStorage.getItem(SESSION_STORAGE_KEY) || '{}');
    return { ...storedSession, token };
  } catch {
    return { token };
  }
}

function storeSession(session) {
  window.sessionStorage.setItem(AUTH_TOKEN_STORAGE_KEY, session.token);
  window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
    expiresAt: session.expiresAt,
    telegramId: session.telegramId,
    telegramUsername: session.telegramUsername,
  }));
}

function clearStoredSession() {
  window.sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
  applyAuthToken('');
}

function LoadingPage() {
  return <PublicLayout><div className="public-loading" role="status">Открываем личный кабинет…</div></PublicLayout>;
}

function App() {
  const [session, setSession] = useState(null);
  const [isRestoring, setIsRestoring] = useState(true);
  const [restoreError, setRestoreError] = useState(false);
  const [restoreAttempt, setRestoreAttempt] = useState(0);
  const isLoginPage = window.location.pathname.replace(/\/+$/, '') === '/login';

  useEffect(() => {
    const interceptorId = httpClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          clearStoredSession();
          window.history.replaceState(null, '', '/login');
          setSession(null);
        }
        return Promise.reject(error);
      },
    );
    return () => httpClient.interceptors.response.eject(interceptorId);
  }, []);

  useEffect(() => {
    const storedSession = readStoredSession();
    if (!storedSession) {
      setIsRestoring(false);
      return;
    }
    const controller = new AbortController();
    applyAuthToken(storedSession.token);
    setIsRestoring(true);
    setRestoreError(false);
    httpClient.get('/api/identity/me', { signal: controller.signal })
      .then(() => {
        if (!controller.signal.aborted) setSession(storedSession);
      })
      .catch((error) => {
        if (!controller.signal.aborted && error.response?.status !== 401) setRestoreError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsRestoring(false);
      });
    return () => controller.abort();
  }, [restoreAttempt]);

  useEffect(() => {
    document.title = session ? 'notreen.vpn' : isLoginPage ? 'Вход — Notreen' : 'Notreen — личное пространство';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', session ? '#121212' : '#f5f4ee');
  }, [session, isLoginPage]);

  const handleLogin = async ({ code }) => {
    const response = await httpClient.post('/api/identity/login', { code });
    storeSession(response.data);
    applyAuthToken(response.data.token);
    window.history.replaceState(null, '', '/');
    setSession(response.data);
  };

  const handleLogout = () => {
    clearStoredSession();
    window.history.replaceState(null, '', '/');
    setRestoreError(false);
    setSession(null);
  };

  if (isRestoring) return <LoadingPage />;
  if (restoreError) {
    return <PublicLayout><div className="public-loading" role="alert">
      <p>Не удалось проверить вход. Проверьте соединение и попробуйте ещё раз.</p>
      <button className="public-primary-button" onClick={() => setRestoreAttempt((value) => value + 1)}>Повторить</button>
      <button className="public-back-link" onClick={handleLogout}>Вернуться на главную</button>
    </div></PublicLayout>;
  }
  if (session) {
    return <Suspense fallback={<LoadingPage />}><MemberPage session={session} onLogout={handleLogout} /></Suspense>;
  }
  return isLoginPage ? <LoginPage onLogin={handleLogin} /> : <LandingPage />;
}

export default App;
